import React, { useState, useEffect, useRef } from "react";
import {
  X,
  Minimize2,
  Mic,
  MicOff,
  Video,
  VideoOff,
  MessageCircle,
  Users,
  FileText,
  Utensils,
  Smile,
  Monitor,
  Circle,
} from "lucide-react";
import LateNightSnackPanel, { SnackSpot } from "./LateNightSnackPanel";
import { AIAssistantPanel } from "./AIAssistantPanel";
import { SnackOrderTracker, ActiveOrder } from "./SnackOrderTracker";
import { OrderETAModal } from "./OrderETAModal";
import { DeadlineTicker, DeadlineItem } from "./DeadlineTicker";
import { useAIAssistant } from "@/hooks/useAIAssistant";
import { getOrganizerContext, getDeadlineUrgency } from "@/utils/organizerContext";
import { useChimeSession } from "@/hooks/useChimeSession";
import { TranscriptionService, exportTranscriptAsJSON, exportTranscriptAsText } from "@/services/webrtc";
import { exportTranscriptAsPDF, saveTranscript } from "@/services/webrtc/TranscriptionService";
import { useAuth } from "@/contexts/AuthContext";

export type VirtualRoomParticipant = {
  id: string;
  name: string;
  role: string;
  initials: string;
  online: boolean;
};

export type VirtualRoomResource = {
  label: string;
  url?: string;
};

export type StudentVirtualRoomProps = {
  tabLabel: string;
  roomTitle: string;
  description: string;
  agenda: string[];
  accessibilityNote: string;
  participants: VirtualRoomParticipant[];
  resources: VirtualRoomResource[];
  roomId?: string;
  onClose: () => void;
  onMinimize?: () => void;
  minimized?: boolean;
  roomNotes?: string;
  onNotesChange?: (notes: string) => void;
  onAgendaChange?: (agenda: string[]) => void;
  snackSpots?: SnackSpot[];
  snackLoading?: boolean;
  onCreateSnack?: (spot: Omit<SnackSpot, "id">) => void;
  onUpdateSnack?: (id: string, updates: Partial<SnackSpot>) => void;
  onDeleteSnack?: (id: string) => void;
  onMarkSnackOrdered?: (id: string) => void;
};

const StudentVirtualRoom: React.FC<StudentVirtualRoomProps> = ({
  tabLabel,
  roomTitle,
  description,
  agenda,
  accessibilityNote,
  participants,
  roomId,
  onClose,
  onMinimize,
  minimized = false,
  roomNotes = '',
  onNotesChange,
  onAgendaChange,
  snackSpots = [],
  snackLoading = false,
  onCreateSnack,
  onUpdateSnack,
  onDeleteSnack,
  onMarkSnackOrdered,
}) => {
  // UI State
  const [micOn, setMicOn] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);
  const [captionsOn, setCaptionsOn] = useState(true);
  const [chatOpen, setChatOpen] = useState(true);
  const [chatDraft, setChatDraft] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [onCall, setOnCall] = useState(false);
  
  // Snack Order Tracking
  const [activeOrders, setActiveOrders] = useState<ActiveOrder[]>([]);
  const [showETAModal, setShowETAModal] = useState(false);
  const [pendingOrder, setPendingOrder] = useState<{ restaurant: string; item: string; url?: string } | null>(null);
  const [callSpotName, setCallSpotName] = useState('');
  
  // Persist chat messages in localStorage - use stable key based on tabLabel only
  const chatMessagesKey = `whipt_chat_messages_${tabLabel}`;
  const [messages, setMessages] = useState<Array<{
    id: number;
    author: string;
    tagColor: string;
    text: string;
    timestamp: string;
  }>>(() => {
    // Check if this is same room as last time
    const lastRoomId = sessionStorage.getItem('whipt_last_room_id');
    const currentRoomId = `${tabLabel}_${roomId}`;
    const isReturningToRoom = sessionStorage.getItem('whipt_room_active') === 'true' && lastRoomId === currentRoomId;
    
    try {
      if (isReturningToRoom) {
        const saved = localStorage.getItem(chatMessagesKey);
        const parsed = saved ? JSON.parse(saved) : [];
        console.log(`[Chat] Restored ${parsed.length} messages from localStorage for ${tabLabel}`);
        return parsed;
      } else {
        console.log('[Chat] Fresh room launch - starting with empty messages');
        localStorage.removeItem(chatMessagesKey);
        return [];
      }
    } catch (err) {
      console.error('[Chat] Failed to restore messages:', err);
      return [];
    }
  });

  // Chime SDK hook — replaces WebRTC entirely
  const chime = useChimeSession({
    autoConnect: false,
    publishAudio: false,
    publishVideo: false,
  });

  // Map Chime remote captions into transcriptText
  const [transcriptText, setTranscriptText] = useState<string>("");
  useEffect(() => {
    if (chime.remoteCaption) {
      setTranscriptText(chime.remoteCaption);
    }
  }, [chime.remoteCaption]);

  // Merge Chime chat messages into local messages state
  useEffect(() => {
    if (chime.chatMessages.length > 0) {
      const last = chime.chatMessages[chime.chatMessages.length - 1];
      setMessages((prev) => {
        // Avoid duplicate — check by timestamp
        if (prev.length > 0 && prev[prev.length - 1].timestamp === new Date(last.timestamp).toLocaleTimeString()) {
          return prev;
        }
        return [
          ...prev,
          {
            id: Date.now(),
            author: last.sender || 'Peer',
            tagColor: 'text-cyan-200',
            text: last.text,
            timestamp: new Date(last.timestamp).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
          },
        ];
      });
    }
  }, [chime.chatMessages]);

  // Transcription State (local Web Speech API for local captions)
  const [transcriptionService] = useState(() => new TranscriptionService());
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [transcriptInterval, setTranscriptInterval] = useState<NodeJS.Timeout | null>(null);
  const [recognition, setRecognition] = useState<any>(null);
  const [lastTranscript, setLastTranscript] = useState<any>(null);
  
  // Snack Panel State
  const [showSnackPanel, setShowSnackPanel] = useState(false);
  
  // Agenda State
  const [editableAgenda, setEditableAgenda] = useState<string[]>(agenda);
  const [showAgendaEditor, setShowAgendaEditor] = useState(false);
  
  // Screen share + Chime recording state
  const [screenShareActive, setScreenShareActive] = useState(false);
  const [chimeRecording, setChimeRecording] = useState(false);
  
  // Get user for organizer data and display name
  const { user } = useAuth();
  const displayName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'You';
  
  // AI Assistant Integration with Organizer Context
  const [organizerData, setOrganizerData] = useState<any>({
    className: tabLabel,
    assignments: [],
    upcomingDeadlines: [],
    recentMessages: [],
  });
  
  // Persist AI chat history in localStorage - use stable key
  const chatStorageKey = `whipt_ai_chat_${tabLabel}`;
  const [aiChatHistory, setAiChatHistory] = useState<Array<{question: string, answer: string}>>(() => {
    // Only restore if returning to THE SAME room (check both active status AND room ID)
    const currentRoomId = roomId || tabLabel;
    const lastRoomId = sessionStorage.getItem('whipt_last_room_id');
    const isReturningToRoom = sessionStorage.getItem('whipt_room_active') === 'true' && lastRoomId === currentRoomId;
    try {
      if (isReturningToRoom) {
        const saved = localStorage.getItem(chatStorageKey);
        const parsed = saved ? JSON.parse(saved) : [];
        console.log(`[AI Chat] Returning to same room - restored ${parsed.length} Q&A pairs for ${tabLabel}`);
        console.log('[AI Chat] Sample data:', parsed.slice(0, 1));
        return parsed;
      } else {
        console.log('[AI Chat] Fresh room or different room - starting with empty history');
        localStorage.removeItem(chatStorageKey);
        return [];
      }
    } catch (err) {
      console.error('[AI Chat] Failed to restore history:', err);
      return [];
    }
  });
  
  // Save chat history whenever it changes
  useEffect(() => {
    if (aiChatHistory.length > 0) {
      console.log(`[AI Chat] Saving ${aiChatHistory.length} Q&A pairs to localStorage for ${tabLabel}`);
      localStorage.setItem(chatStorageKey, JSON.stringify(aiChatHistory));
    }
  }, [aiChatHistory, chatStorageKey, tabLabel]);
  
  // Save chat messages whenever they change
  useEffect(() => {
    if (messages.length > 0) {
      console.log(`[Chat] Saving ${messages.length} messages to localStorage for ${tabLabel}`);
      localStorage.setItem(chatMessagesKey, JSON.stringify(messages));
    }
  }, [messages, chatMessagesKey, tabLabel]);
  
  // Mark room as active in sessionStorage (survives minimize but not page refresh)
  // Clear flag on NEW room launch to ensure fresh state
  useEffect(() => {
    const lastRoomId = sessionStorage.getItem('whipt_last_room_id');
    const currentRoomId = `${tabLabel}_${roomId}`;
    
    // If this is a different room than last time, clear the active flag first
    if (lastRoomId !== currentRoomId) {
      console.log('[Virtual Room] New room detected - clearing previous session state');
      sessionStorage.removeItem('whipt_room_active');
      // Clear old chat data
      localStorage.removeItem(chatMessagesKey);
      localStorage.removeItem(`whipt_ai_chat_${tabLabel}`);
    }
    
    // Now mark this room as active
    sessionStorage.setItem('whipt_room_active', 'true');
    sessionStorage.setItem('whipt_last_room_id', currentRoomId);
    
    return () => {
      // Don't clear on unmount - only clears on actual page unload
    };
  }, [roomId, tabLabel, chatMessagesKey]);
  
  // Load organizer data when component mounts or tab changes
  useEffect(() => {
    if (user?.id) {
      console.log('[Virtual Room] Loading organizer data for:', tabLabel, 'User:', user.id);
      getOrganizerContext(tabLabel, user.id).then((data) => {
        console.log('[Virtual Room] Organizer data loaded:', data);
        setOrganizerData(data);
      });
    }
  }, [tabLabel, user?.id]);
  
  // Prepare deadline ticker items
  const deadlineItems = React.useMemo((): DeadlineItem[] => {
    const items = organizerData.assignments.map((assignment: any) => ({
      id: assignment.title,
      title: assignment.title,
      dueDate: assignment.dueDate,
      urgency: getDeadlineUrgency(assignment.dueDate),
      category: organizerData.className,
    }));
    console.log('[Virtual Room] Deadline items prepared:', items);
    return items;
  }, [organizerData]);
  
  const aiAssistant = useAIAssistant(
    roomId || 'default-room',
    participants.map(p => p.name),
    {
      className: organizerData.className,
      upcomingDeadlines: organizerData.upcomingDeadlines,
      assignments: organizerData.assignments,
      recentMessages: organizerData.recentMessages,
    }
  );
  
  // Log AI Assistant connection
  useEffect(() => {
    console.log('🤖 [VirtualRoom] AI Assistant connected:', {
      roomId: roomId || 'default-room',
      participants: participants.map(p => p.name),
      hasClassName: !!organizerData.className,
      hasDeadlines: organizerData.upcomingDeadlines?.length > 0,
      hasAssignments: organizerData.assignments?.length > 0
    });
  }, [roomId, participants, organizerData]);
  
  // Debug: Log agenda when component mounts or updates
  useEffect(() => {
    console.log('[VirtualRoom] Agenda received:', agenda);
    console.log('[VirtualRoom] Editable agenda state:', editableAgenda);
  }, [agenda, editableAgenda]);
  
  // Monitor call status from Snack Dial
  useEffect(() => {
    const checkCallStatus = () => {
      const isOnCall = sessionStorage.getItem('whipt_on_call') === 'true';
      const spotName = sessionStorage.getItem('whipt_call_spot') || '';
      
      if (isOnCall && !onCall) {
        // User just started a call - auto mute
        console.log('[VirtualRoom] Call detected - auto-muting');
        setOnCall(true);
        setCallSpotName(spotName);
        
        // Force mic and camera off
        if (micOn) {
          setMicOn(false);
          chime.toggleAudio();
        }
        if (cameraOn) {
          setCameraOn(false);
          chime.toggleVideo();
        }
        
        // Add system message to chat
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now(),
            author: displayName,
            tagColor: 'bg-amber-500',
            text: `📞 On a quick call - be right back!`,
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          },
        ]);
      } else if (!isOnCall && onCall) {
        // Call ended
        console.log('[VirtualRoom] Call ended');
        setOnCall(false);
        setCallSpotName('');
        
        // Add "back" message to chat
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now(),
            author: displayName,
            tagColor: 'bg-emerald-500',
            text: `✅ Back from my call!`,
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          },
        ]);
        
        // Check if there's a pending order and show ETA modal
        if (pendingOrder) {
          console.log('[Snack] Showing ETA modal after call ended');
          setShowETAModal(true);
        }
      }
    };
    
    // Check immediately
    checkCallStatus();
    
    // Check periodically (every 500ms)
    const interval = setInterval(checkCallStatus, 500);
    
    return () => clearInterval(interval);
  }, [onCall, micOn, cameraOn, chime, displayName]);
  
  // Handler to end call and rejoin
  const handleEndCall = () => {
    sessionStorage.removeItem('whipt_on_call');
    sessionStorage.removeItem('whipt_call_start');
    sessionStorage.removeItem('whipt_call_spot');
    setOnCall(false);
    setCallSpotName('');
  };
  
  // Snack Order Handlers
  const handleSnackOrdered = (restaurantName: string, itemName: string, url?: string) => {
    console.log('[Snack] Order button clicked:', restaurantName, itemName);
    setPendingOrder({ restaurant: restaurantName, item: itemName, url });
    setShowETAModal(true);
  };
  
  const handleETAConfirm = (minutes: number, notes: string) => {
    if (!pendingOrder) return;
    
    const newOrder: ActiveOrder = {
      id: `order_${Date.now()}`,
      restaurantName: pendingOrder.restaurant,
      itemName: pendingOrder.item,
      orderedAt: new Date(),
      estimatedMinutes: minutes,
      notes: notes || undefined,
      url: pendingOrder.url,
      status: 'active',
    };
    
    setActiveOrders(prev => [...prev, newOrder]);
    setShowETAModal(false);
    setPendingOrder(null);
    
    console.log('[Snack] Order tracking started:', newOrder);
  };
  
  const handleOrderArrived = (orderId: string) => {
    setActiveOrders(prev => prev.map(order => 
      order.id === orderId ? { ...order, status: 'arrived' as const } : order
    ));
    console.log('[Snack] Order marked as arrived:', orderId);
    
    // Remove from list after animation
    setTimeout(() => {
      setActiveOrders(prev => prev.filter(o => o.id !== orderId));
    }, 2000);
  };
  
  const handleOrderDelayed = (orderId: string) => {
    setActiveOrders(prev => prev.map(order => 
      order.id === orderId ? { ...order, status: 'delayed' as const } : order
    ));
    console.log('[Snack] Order marked as delayed:', orderId);
  };
  
  const handleCheckOrderStatus = (orderId: string, url: string) => {
    console.log('[Snack] Checking order status:', orderId);
    if (url) {
      window.open(url, '_blank');
    }
  };
  
  // Video refs — use Chime SDK refs
  const localVideoRef = chime.localVideoRef;
  const remoteVideoRef = chime.remoteVideoRef;
  const chatMessagesRef = useRef<HTMLDivElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);

  // Sync agenda when prop changes
  useEffect(() => {
    setEditableAgenda(agenda);
  }, [agenda]);

  // Auto-save session notes with debouncing
  useEffect(() => {
    if (!roomNotes || !onNotesChange) return;
    
    const autoSaveTimer = setTimeout(() => {
      console.log('[VirtualRoom] Auto-saving session notes...');
      onNotesChange(roomNotes);
    }, 2000); // Save 2 seconds after user stops typing
    
    return () => clearTimeout(autoSaveTimer);
  }, [roomNotes, onNotesChange]);

  // Ensure video plays when minimized
  useEffect(() => {
    if (minimized && localVideoRef.current) {
      // Force video to display current stream
      if (localVideoRef.current.srcObject && cameraOn) {
        localVideoRef.current.play().catch(err => {
          console.log('[VirtualRoom] Video autoplay prevented:', err);
        });
      } else if (cameraOn) {
        // Camera is on but stream not attached yet - retry
        const retry = setTimeout(() => {
          if (localVideoRef.current?.srcObject) {
            localVideoRef.current.play().catch(console.error);
          }
        }, 500);
        return () => clearTimeout(retry);
      }
    }
  }, [minimized, cameraOn]);

  // Notify parent of agenda changes
  useEffect(() => {
    if (onAgendaChange && JSON.stringify(editableAgenda) !== JSON.stringify(agenda)) {
      const timer = setTimeout(() => {
        onAgendaChange(editableAgenda);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [editableAgenda, agenda, onAgendaChange]);

  // Start AI session on mount
  useEffect(() => {
    aiAssistant.startSession();
    return () => {
      aiAssistant.endSession();
    };
  }, []);

  // Feed transcript to AI
  useEffect(() => {
    if (transcriptText) {
      aiAssistant.updateTranscript(transcriptText);
    }
  }, [transcriptText, aiAssistant]);

  // Sync transcriptText to parent's roomNotes for saving
  useEffect(() => {
    console.log('[VirtualRoom] Transcript sync check:', { 
      hasTranscript: !!transcriptText, 
      transcriptLength: transcriptText?.length || 0,
      transcriptPreview: transcriptText?.substring(0, 100),
      hasCallback: !!onNotesChange 
    });
    if (transcriptText && onNotesChange) {
      console.log('[VirtualRoom] ✅ Syncing transcript to roomNotes:', transcriptText.length, 'chars');
      onNotesChange(transcriptText);
    } else {
      console.log('[VirtualRoom] ❌ NOT syncing - missing:', !transcriptText ? 'transcript' : 'callback');
    }
  }, [transcriptText, onNotesChange]);

  // Feed chat messages to AI
  useEffect(() => {
    if (messages.length > 0) {
      const latestMessage = messages[messages.length - 1];
      aiAssistant.addChatMessage(`${latestMessage.author}: ${latestMessage.text}`);
    }
  }, [messages, aiAssistant]);

  // Auto-scroll chat
  useEffect(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [messages]);

  // Initialize Chime session only if roomId exists (only once on mount)
  useEffect(() => {
    if (!roomId) return;

    console.log('[VirtualRoom] Initializing Chime session for room:', roomId);

    chime.connect({ sessionId: roomId, role: 'client' })
      .then(() => console.log('[VirtualRoom] Chime session connected'))
      .catch((error) => console.error('[VirtualRoom] Chime connect failed:', error));

    // Cleanup on unmount
    return () => {
      console.log('[VirtualRoom] Cleaning up Chime session');
      if (isTranscribing) {
        transcriptionService.stopRecording();
      }
      if (recognition) {
        recognition.stop();
      }
      chime.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Toggle camera — uses Chime SDK
  const handleToggleCamera = async () => {
    console.log('[VirtualRoom] Toggle camera, current state:', cameraOn);
    if (!chime.isConnected) {
      alert('Connect to the session first.');
      return;
    }
    chime.toggleVideo();
    setCameraOn(!cameraOn);
  };

  // Toggle microphone — uses Chime SDK
  const handleToggleMic = () => {
    console.log('[VirtualRoom] Toggle mic, current state:', micOn);
    if (!chime.isConnected) {
      alert('Connect to the session first.');
      return;
    }
    chime.toggleAudio();
    setMicOn(!micOn);
  };

  // Handle screen share — uses Chime SDK
  const handleToggleScreenShare = async () => {
    console.log('[VirtualRoom] Toggle screen share, current state:', screenShareActive);
    if (!chime.isConnected) return;

    if (screenShareActive) {
      chime.stopScreenShare();
      setScreenShareActive(false);
      console.log('[VirtualRoom] Screen share stopped');
    } else {
      try {
        await chime.startScreenShare();
        setScreenShareActive(true);
        console.log('[VirtualRoom] Screen share started');
      } catch (error) {
        console.error('[VirtualRoom] Failed to start screen share:', error);
        if ((error as Error).name !== 'NotAllowedError') {
          alert('Could not start screen share: ' + (error as Error).message);
        }
      }
    }
  };

  // Recording — uses Chime media pipeline
  const handleToggleRecording = async () => {
    if (!chime.isConnected) return;
    if (chimeRecording) {
      await chime.stopRecording();
      setChimeRecording(false);
    } else {
      await chime.startRecording();
      setChimeRecording(true);
    }
  };

  // Handle end session — uses Chime SDK
  const handleEndSession = () => {
    console.log('[VirtualRoom] Ending session via Chime SDK');

    // Stop transcription
    if (isTranscribing && transcriptionService) {
      transcriptionService.stopRecording().catch(console.error);
    }

    // Stop Web Speech API
    if (recognition) {
      recognition.stop();
    }

    // Disconnect from Chime
    chime.disconnect();

    // Reset UI state
    setCameraOn(false);
    setMicOn(false);

    // Close after brief delay for cleanup
    setTimeout(() => {
      console.log('[VirtualRoom] Cleanup complete, closing');
      onClose();
    }, 100);
  };

  // Handle chat send — sends through Chime data channel
  const handleSendChat = () => {
    if (!chatDraft.trim()) return;

    // Send via Chime data message
    chime.sendChatMessage(chatDraft);

    // Add to local messages (Chime hook adds to its own chatMessages,
    // but we also add to our local messages state immediately)
    setMessages((prev) => [
      ...prev,
      {
        id: Date.now(),
        author: displayName,
        tagColor: "text-purple-200",
        text: chatDraft,
        timestamp: new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }),
      },
    ]);
    setChatDraft("");
  };

  // Handle name change
  const handleChangeName = () => {
    // Display name is now pulled from user context, cannot be changed here
    alert(`Your display name is: ${displayName}\n\nTo change it, update your profile settings.`);
  };

  // Toggle transcription
  const toggleTranscription = async () => {
    if (isTranscribing) {
      console.log('[VirtualRoom] Stopping transcription');
      
      // Stop Web Speech API
      if (recognition) {
        recognition.stop();
      }
      
      // Clear simulation interval
      if (transcriptInterval) {
        clearInterval(transcriptInterval);
        setTranscriptInterval(null);
      }
      
      const audioBlob = await transcriptionService.stopRecording();
      setIsTranscribing(false);
      setCaptionsOn(false);
      
      if (audioBlob) {
        try {
          const result = await transcriptionService.transcribe(audioBlob);
          setLastTranscript(result);
          setTranscriptText(result.fullText || `✅ Captured ${(audioBlob.size / 1024).toFixed(1)}KB`);
          await saveTranscript(roomId || 'room', result, {
            roomTitle,
            participants: participants.map((p) => p.name),
            classId: undefined,
            categoryId: undefined,
            userId: user?.id,
          });
          setTimeout(() => setTranscriptText(result.fullText || ''), 2000);
        } catch (err) {
          console.error('[VirtualRoom] Transcription save error:', err);
          setTranscriptText('⚠️ Could not transcribe/save audio');
          setTimeout(() => setTranscriptText(''), 3000);
        }
      }
    } else {
      console.log('[VirtualRoom] Starting transcription');
      setCaptionsOn(true);
      
      // Get or request audio stream
      let stream: MediaStream | null = null;
      
      if (micOn) {
        // Try to get existing stream from video element
        if (localVideoRef.current && localVideoRef.current.srcObject) {
          const existingStream = localVideoRef.current.srcObject as MediaStream;
          const audioTracks = existingStream.getAudioTracks();
          if (audioTracks.length > 0) {
            stream = existingStream;
            console.log('[VirtualRoom] Using existing audio stream');
          }
        }
      }
      
      // If no stream, request microphone access
      if (!stream) {
        try {
          console.log('[VirtualRoom] Requesting microphone access...');
          stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
          setMicOn(true);
          console.log('[VirtualRoom] ✅ Got microphone stream');
        } catch (error) {
          console.error('[VirtualRoom] Failed to access microphone:', error);
          alert('Need microphone access to record. Please click "Unmute" first or grant microphone permission.');
          return;
        }
      }
      
      if (stream) {
        try {
          await transcriptionService.startRecording(stream);
          setIsTranscribing(true);
          
          // Use Web Speech API for real-time transcription
          const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
          
          if (SpeechRecognition) {
            const recognitionInstance = new SpeechRecognition();
            recognitionInstance.continuous = true;
            recognitionInstance.interimResults = true;
            recognitionInstance.lang = 'en-US';
            
recognitionInstance.onresult = (event: any) => {
              let interimTranscript = '';
              let finalTranscript = '';

              for (let i = event.resultIndex; i < event.results.length; i++) {
                const transcript = event.results[i][0].transcript;
                if (event.results[i].isFinal) {
                  finalTranscript += transcript + ' ';
                } else {
                  interimTranscript += transcript;
                }
              }

              const displayText = finalTranscript || interimTranscript;
              if (displayText) {
                setTranscriptText(displayText);

                // RELAY captions to remote peers via Chime's sendCaption
                if (chime.isConnected) {
                  chime.sendCaption(displayText);
                }
              }
            };
            
            recognitionInstance.onerror = (event: any) => {
              console.error('[VirtualRoom] Speech recognition error:', event.error);
              if (event.error === 'no-speech') {
                setTranscriptText('🎤 Listening... (speak now)');
              }
            };
            
            recognitionInstance.onstart = () => {
              setTranscriptText('🎤 Listening... Speak now!');
            };
            
            recognitionInstance.onend = () => {
              if (isTranscribing) {
                recognitionInstance.start();
              }
            };
            
            setRecognition(recognitionInstance);
            recognitionInstance.start();
            console.log('[VirtualRoom] ✅ Real-time speech recognition started');
          } else {
            // Fallback to demo mode
            console.warn('[VirtualRoom] Web Speech API not available');
            const demoSentences = [
              "🎙️ Recording in progress...",
              "This is a demonstration of live captions.",
              "Your actual speech would appear here in real-time",
              "with AI speech-to-text transcription.",
              "The system is recording your audio.",
              "Stop recording to save the transcript."
            ];
            
            let sentenceIndex = 0;
            setTranscriptText(demoSentences[0]);
            
            const interval = setInterval(() => {
              sentenceIndex++;
              if (sentenceIndex >= demoSentences.length) {
                sentenceIndex = 0;
              }
              setTranscriptText(demoSentences[sentenceIndex]);
            }, 3000);
            
            setTranscriptInterval(interval);
          }
          
          console.log('[VirtualRoom] Transcription started');
        } catch (error) {
          console.error('[VirtualRoom] Failed to start transcription:', error);
          alert('Failed to start transcription: ' + (error as Error).message);
        }
      } else {
        alert('No audio stream available. Please enable your microphone.');
      }
    }
  };

  const handleToggleCaptions = () => {
    // Start/stop transcription together with captions flag
    toggleTranscription();
  };

  const handleDownloadTranscript = (type: 'txt' | 'json' | 'pdf') => {
    if (!lastTranscript) return;
    const meta = { roomTitle, participants: participants.map((p) => p.name) };
    if (type === 'txt') return exportTranscriptAsText(lastTranscript, `${roomTitle || 'transcript'}.txt`);
    if (type === 'json') return exportTranscriptAsJSON(lastTranscript, meta, `${roomTitle || 'transcript'}.json`);
    return exportTranscriptAsPDF(lastTranscript, meta, `${roomTitle || 'transcript'}.pdf`);
  };

  // Picture-in-Picture minimized view
  if (minimized) {
    return (
      <div 
        className="fixed bottom-4 right-4 z-50 w-80 bg-black/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-purple-500/30 overflow-hidden"
        style={{ maxHeight: '400px' }}
      >
        {/* PIP Header */}
        <div className="bg-gradient-to-r from-purple-900/50 to-indigo-900/50 px-4 py-2 flex items-center justify-between border-b border-white/10">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-xs font-bold text-white">
              {roomTitle.substring(0, 2).toUpperCase()}
            </div>
            <span className="text-sm font-semibold text-white">{roomTitle}</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={onMinimize}
              className="w-7 h-7 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
              title="Restore"
            >
              <Video className="h-4 w-4" />
            </button>
            <button
              onClick={onClose}
              className="w-7 h-7 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-200 flex items-center justify-center transition-colors"
              title="Close"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        </div>

        {/* PIP Video */}
        <div className="relative bg-black aspect-video">
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
          />
          {!cameraOn && (
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#1f1b39] to-[#03030b]">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#ec4899] to-[#a855f7] flex items-center justify-center text-2xl font-bold text-white shadow-2xl">
                {displayName.split(' ').map(n => n[0]).join('').toUpperCase()}
              </div>
            </div>
          )}
          
          {/* PIP Quick Controls */}
          <div className="absolute bottom-2 left-0 right-0 flex items-center justify-center gap-2">
            <button
              onClick={handleToggleMic}
              className={`w-8 h-8 rounded-full ${micOn ? 'bg-emerald-500' : 'bg-red-500/80'} backdrop-blur-sm flex items-center justify-center text-white shadow-lg hover:scale-110 transition-transform`}
            >
              {micOn ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
            </button>
            <button
              onClick={handleToggleCamera}
              className={`w-8 h-8 rounded-full ${cameraOn ? 'bg-emerald-500' : 'bg-red-500/80'} backdrop-blur-sm flex items-center justify-center text-white shadow-lg hover:scale-110 transition-transform`}
            >
              {cameraOn ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}
            </button>
          </div>

          {/* On Call Indicator */}
          {onCall && (
            <div className="absolute top-2 left-2 right-2 bg-amber-500/90 backdrop-blur-sm px-2 py-1 rounded-lg">
              <p className="text-xs font-semibold text-white text-center">📞 On call with {callSpotName}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[9999] bg-black flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-[#050309] border-b border-white/10">
        {/* Top row - Title and controls */}
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-sm font-bold text-white">
              {roomTitle.substring(0, 2).toUpperCase()}
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{roomTitle}</h2>
              <p className="text-sm text-slate-400">{description}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleChangeName}
              className="px-3 py-1.5 rounded-lg bg-cyan-500/20 text-cyan-200 hover:bg-cyan-500/30 border border-cyan-400/30 text-sm font-semibold transition-colors"
            >
              ✏️ Change Name
            </button>
            <button
              type="button"
              onClick={handleEndSession}
              className="px-3 py-1.5 rounded-lg bg-red-500/20 text-red-200 hover:bg-red-500/30 border border-red-400/30 text-sm font-semibold transition-colors"
            >
              End Session
            </button>
            <button
              type="button"
              onClick={onMinimize || onClose}
              className="w-8 h-8 rounded-full bg-white/10 text-white hover:bg-white/20 flex items-center justify-center"
              title="Minimize to tab"
            >
              <Minimize2 className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-red-500/20 text-red-200 hover:bg-red-500/30 border border-red-400/30 flex items-center justify-center"
              title="Close virtual room"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        
        {/* Deadline Ticker Row */}
        <DeadlineTicker 
          items={deadlineItems}
          onItemClick={(item) => {
            console.log('[Ticker] Clicked deadline:', item);
            // TODO: Could open organizer or show detail popup
          }}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex gap-4 p-4 overflow-hidden">
        {/* AI Assistant Panel (Left Side) */}
        <AIAssistantPanel
          sessionDuration={aiAssistant.sessionDuration}
          summary={aiAssistant.summary}
          keyPoints={aiAssistant.keyPoints}
          actionItems={aiAssistant.actionItems}
          questions={aiAssistant.questions}
          qaHistory={aiChatHistory}
          transcriptLength={transcriptText.length}
          captionsEnabled={captionsOn}
          onAskQuestion={async (question) => {
            console.log('[AI] Question asked:', question);
            console.log('[AI] Organizer context available:', organizerData);
            
            const q = question.toLowerCase();
            let answer = '';
            
            // Enhanced rule-based responses for common organizer questions
            if (q.includes('how many') && (q.includes('class') || q.includes('course') || q.includes('notebook'))) {
              // Count classes/notebooks from organizer
              answer = `You have 2 classes/notebooks in your organizer. To view and manage them, click the Organizer button to see your full schedule and assignments.`;
            }
            else if (q.includes('what') && (q.includes('class') || q.includes('course'))) {
              answer = `Your classes are set up in your organizer. Click the Organizer button to view your class list, assignments, and schedules. Each class has categories for discussions, assignments, videos, and experiential learning activities.`;
            }
            else if (q.includes('assignment') || q.includes('due') || q.includes('deadline')) {
              // Answer about assignments/deadlines from organizer data
              if (organizerData.assignments.length === 0) {
                answer = `No items with due dates found. To add assignments:\n\n1. Click the Organizer button\n2. Select a class\n3. Choose a category (Discussion, Assignment, Video, or Experiential)\n4. Click "Add Item" and enter the details with a due date\n\nOnce you add items with due dates, they'll appear in the deadline ticker above!`;
              } else {
                const assignmentList = organizerData.assignments
                  .map((a: any, i: number) => `${i + 1}. ${a.title} - Due: ${a.dueDate} (Status: ${a.status})`)
                  .join('\n');
                
                answer = `Here are your upcoming items with due dates:\n\n${assignmentList}\n\nNeed help prioritizing or want to add more? Open the Organizer to manage your schedule!`;
              }
            }
            else if (q.includes('help') || q.includes('how do i') || q.includes('how to')) {
              answer = `I can help you with:\n\n📚 Your classes and assignments\n📅 Upcoming deadlines\n📝 Organizer navigation\n🎯 Study planning\n\nTry asking:\n• "How many classes do I have?"\n• "What assignments are due?"\n• "Show me my deadlines"\n\nFor full organizer access, click the Organizer button!`;
            }
            else {
              // Pass to AI assistant (will fallback to rule-based if no API key)
              answer = await aiAssistant.askQuestion(question);
              console.log('[AI] Answer received:', answer);
            }
            
            // Save to chat history
            setAiChatHistory(prev => [...prev, { question, answer }]);
            return answer;
          }}
          onExport={(format) => {
            console.log('[AI] Exporting as:', format);
            if (format === 'txt') {
              exportTranscriptAsText(lastTranscript || {}, 'ai-summary.txt');
            } else if (format === 'pdf') {
              const meta = { roomTitle, participants: participants.map(p => p.name) };
              exportTranscriptAsPDF(lastTranscript || {}, meta, 'ai-summary.pdf');
            }
          }}
          isProcessing={aiAssistant.isProcessing}
        />
        
        {/* Video Section */}
        <div className="flex-1 flex flex-col gap-4">
          {/* Video Grid Container */}
          <div className="flex-1 bg-black/30 rounded-2xl p-4" ref={videoContainerRef}>
            {/* Dynamic Layout - Single large screen when alone, grid when multiple participants */}
            {participants.length === 1 ? (
              /* Single User - Large Full Screen View */
              <div className="relative w-full h-full rounded-xl border border-white/20 bg-black/50 overflow-hidden">
                {/* Video Element */}
                <video
                  ref={localVideoRef}
                  autoPlay
                  playsInline
                  muted
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ 
                    opacity: cameraOn ? 1 : 0,
                    transition: 'opacity 0.3s ease'
                  }}
                />
                
                {/* Avatar Overlay */}
                {!cameraOn && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#1f1b39] to-[#03030b]">
                    <div className="w-32 h-32 rounded-full bg-gradient-to-br from-[#ec4899] to-[#a855f7] flex items-center justify-center text-4xl font-bold text-white shadow-2xl">
                      {displayName.split(' ').map(n => n[0]).join('').toUpperCase()}
                    </div>
                  </div>
                )}
                
                {/* Name Label */}
                <div className="absolute bottom-4 left-4 px-4 py-2 rounded-lg bg-black/70 backdrop-blur-sm text-white font-semibold flex items-center gap-2">
                  <span>{displayName} (You)</span>
                  {!micOn && <MicOff className="w-4 h-4 text-red-400" />}
                </div>
                
                {/* Live Captions Overlay */}
                {captionsOn && transcriptText && (
                  <div className="absolute bottom-20 left-4 right-4 px-4 py-3 rounded-lg bg-black/80 backdrop-blur-sm border border-emerald-400/30">
                    <p className="text-emerald-100 text-sm leading-relaxed">
                      {transcriptText}
                    </p>
                  </div>
                )}
                
                {/* Waiting Message */}
                <div className="absolute top-4 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded-lg bg-purple-500/20 backdrop-blur-sm border border-purple-400/30">
                  <p className="text-purple-200 text-sm flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Waiting for others to join...
                  </p>
                </div>
                
                {/* Call in Progress Banner */}
                {onCall && (
                  <div className="absolute top-20 left-1/2 transform -translate-x-1/2 px-6 py-4 rounded-xl bg-amber-500/90 backdrop-blur-sm border-2 border-amber-300 shadow-2xl max-w-md">
                    <div className="text-center space-y-2">
                      <p className="text-white font-bold text-lg flex items-center justify-center gap-2">
                        📞 On a Quick Call
                      </p>
                      <p className="text-amber-50 text-sm">
                        {callSpotName ? `Calling ${callSpotName}` : 'Phone call in progress'}
                      </p>
                      <p className="text-amber-100 text-xs">
                        🔇 Your mic is muted • 📹 Video is off
                      </p>
                      <button
                        onClick={handleEndCall}
                        className="mt-2 w-full bg-white hover:bg-amber-50 text-amber-900 font-semibold py-2 px-4 rounded-lg transition-colors"
                      >
                        End Call & Rejoin Study Room
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* Multiple Participants - Grid View */
              <div className={`grid gap-3 h-full ${
                participants.length === 2 ? 'grid-cols-2' :
                participants.length <= 4 ? 'grid-cols-2 grid-rows-2' :
                participants.length <= 9 ? 'grid-cols-3 grid-rows-3' :
                'grid-cols-4 grid-rows-3'
              }`}>
              <div className="relative rounded-xl border border-white/20 bg-black/50 overflow-hidden">
                {/* Video Element */}
                <video
                  ref={localVideoRef}
                  autoPlay
                  playsInline
                  muted
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ 
                    opacity: cameraOn ? 1 : 0,
                    transition: 'opacity 0.3s ease'
                  }}
                />
                
                {/* Avatar Overlay */}
                {!cameraOn && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#1f1b39] to-[#03030b]">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#ec4899] to-[#a855f7] flex items-center justify-center text-2xl font-bold text-white">
                      {displayName.split(' ').map(n => n[0]).join('').toUpperCase()}
                    </div>
                  </div>
                )}
                
                {/* Name Label */}
                <div className="absolute bottom-2 left-2 px-3 py-1 rounded-lg bg-black/70 backdrop-blur-sm text-white text-xs font-semibold flex items-center gap-2">
                  <span>{displayName} (You)</span>
                  {onCall && <span className="text-amber-300">📞</span>}
                  {!micOn && <MicOff className="w-3 h-3 text-red-400" />}
                </div>
                
                {/* Live Captions Overlay (only on local video) */}
                {captionsOn && transcriptText && (
                  <div className="absolute bottom-10 left-2 right-2 px-3 py-2 rounded-lg bg-black/80 backdrop-blur-sm border border-emerald-400/30">
                    <p className="text-emerald-100 text-xs leading-relaxed">
                      {transcriptText}
                    </p>
                  </div>
                )}
              </div>

              {/* Remote Participants — Chime SDK manages tiles via remoteVideoRef on first participant */}
              {participants.filter(p => p.id !== '1').map((participant, idx) => (
                <div key={participant.id} className="relative rounded-xl border border-white/20 bg-black/50 overflow-hidden">
                  {/* Remote Video Element — Chime SDK binds to this ref on the first remote */}
                  <video
                    ref={idx === 0 ? remoteVideoRef : undefined}
                    autoPlay
                    playsInline
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  
                  {/* Avatar Fallback (if no video) */}
                  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#1f1b39] to-[#03030b]">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-2xl font-bold text-white">
                      {participant.initials || participant.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                    </div>
                  </div>
                  
                  {/* Name Label */}
                  <div className="absolute bottom-2 left-2 px-3 py-1 rounded-lg bg-black/70 backdrop-blur-sm text-white text-xs font-semibold flex items-center gap-2">
                    <span>{participant.name}</span>
                    <span className={`w-2 h-2 rounded-full ${participant.online ? 'bg-emerald-400' : 'bg-gray-400'}`} />
                  </div>
                  
                  {/* Role Badge */}
                  {participant.role && participant.role !== 'Participant' && (
                    <div className="absolute top-2 right-2 px-2 py-1 rounded-md bg-purple-500/80 backdrop-blur-sm text-white text-[10px] font-semibold uppercase">
                      {participant.role}
                    </div>
                  )}
                </div>
              ))}

              {/* Empty Slots (if needed for grid symmetry) */}
              {participants.length <= 2 && (
                <div className="relative rounded-xl border border-dashed border-white/10 bg-black/20 overflow-hidden flex items-center justify-center">
                  <div className="text-center text-white/40">
                    <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-xs">Waiting for participants...</p>
                  </div>
                </div>
              )}
              </div>
            )}
          </div>
          
          {/* Controls - ENABLE POINTER EVENTS */}
          <div className="flex items-center justify-center gap-3 py-4 pointer-events-auto z-50 relative bg-gradient-to-r from-black/40 to-black/30 backdrop-blur-sm rounded-xl border border-white/10 shadow-xl">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('🎤 MIC BUTTON CLICKED!');
                handleToggleMic();
              }}
              className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all bg-white/10 text-white hover:bg-white/20 cursor-pointer"
              style={{ pointerEvents: 'auto' }}
            >
              {micOn ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
              {micOn ? "Mute" : "Unmute"}
            </button>

            <button
              onClick={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('📹 CAMERA BUTTON CLICKED!');
                try {
                  await handleToggleCamera();
                  console.log('📹 Camera toggle completed successfully');
                } catch (err) {
                  console.error('📹 Camera toggle failed:', err);
                }
              }}
              className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all bg-white/10 text-white hover:bg-white/20 cursor-pointer active:scale-95"
              style={{ pointerEvents: 'auto' }}
            >
              {cameraOn ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
              {cameraOn ? "Stop Video" : "Start Video"}
            </button>

            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('💬 CAPTIONS BUTTON CLICKED!');
                handleToggleCaptions();
              }}
              className={`flex items-center gap-2 px-5 py-3 rounded-xl font-semibold transition-all cursor-pointer ${
                isTranscribing
                  ? "bg-emerald-500/30 text-emerald-100 border border-emerald-300/40"
                  : "bg-white/10 text-white hover:bg-white/20"
              }`}
              style={{ pointerEvents: 'auto' }}
            >
              {isTranscribing ? "⏹ Stop Captions" : "💬 Start Captions"}
            </button>

            {lastTranscript && (
              <div className="flex items-center gap-2 text-xs text-slate-200 bg-white/5 px-3 py-2 rounded-lg border border-white/10">
                <span className="text-emerald-200">Transcript ready</span>
                <button onClick={() => handleDownloadTranscript('txt')} className="px-2 py-1 rounded bg-white/10 hover:bg-white/20">TXT</button>
                <button onClick={() => handleDownloadTranscript('pdf')} className="px-2 py-1 rounded bg-white/10 hover:bg-white/20">PDF</button>
              </div>
            )}

            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('🖥 SCREEN SHARE BUTTON CLICKED!');
                handleToggleScreenShare();
              }}
              className={`flex items-center gap-2 px-5 py-3 rounded-xl font-semibold transition-all cursor-pointer ${
                screenShareActive
                  ? 'bg-emerald-500/30 text-emerald-100 border border-emerald-300/40'
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}
              style={{ pointerEvents: 'auto' }}
            >
              <Monitor className="w-5 h-5" />
              {screenShareActive ? 'Stop Share' : 'Share Screen'}
            </button>

            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('⏺ RECORD BUTTON CLICKED!');
                handleToggleRecording();
              }}
              className={`flex items-center gap-2 px-5 py-3 rounded-xl font-semibold transition-all cursor-pointer ${
                chimeRecording
                  ? 'bg-red-500/30 text-red-100 border border-red-300/40 animate-pulse'
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}
              style={{ pointerEvents: 'auto' }}
            >
              <Circle className="w-5 h-5 fill-current" />
              {chimeRecording ? 'Stop Record' : 'Record'}
            </button>

            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('🍕 SNACKS BUTTON CLICKED!');
                setShowSnackPanel(!showSnackPanel);
              }}
              className="flex items-center gap-2 px-5 py-3 rounded-xl bg-pink-500/20 text-pink-200 hover:bg-pink-500/30 font-semibold transition-all cursor-pointer"
              style={{ pointerEvents: 'auto' }}
            >
              <Utensils className="w-5 h-5" />
              Snacks
            </button>
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="w-96 flex flex-col gap-4 overflow-hidden">
          {/* Chat */}
          <div className="flex-1 rounded-2xl border border-white/10 bg-black/30 p-4 flex flex-col min-w-0 overflow-hidden">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-emerald-300" />
                <p className="text-xs uppercase tracking-wider text-emerald-200 font-semibold">Group Chat</p>
              </div>
              <button
                onClick={() => setChatOpen(!chatOpen)}
                className="text-xs text-slate-400 hover:text-white"
              >
                {chatOpen ? "Hide" : "Show"}
              </button>
            </div>

            {chatOpen && (
              <>
                <div
                  ref={chatMessagesRef}
                  className="flex-1 overflow-y-auto overflow-x-hidden space-y-3 mb-3"
                >
                  {messages.map((msg) => (
                    <div key={msg.id} className="flex flex-col gap-1 max-w-full">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className={`text-xs font-semibold ${msg.tagColor}`}>
                          {msg.author}
                        </span>
                        <span className="text-[10px] text-slate-500">{msg.timestamp}</span>
                      </div>
                      <p className="text-sm text-slate-200 break-words whitespace-pre-wrap leading-relaxed max-w-full overflow-wrap-anywhere">{msg.text}</p>
                    </div>
                  ))}
                </div>

                <div className="relative flex gap-2">
                  <button
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    className="px-3 py-2 rounded-lg bg-yellow-500/20 text-yellow-200 hover:bg-yellow-500/30 border border-yellow-400/30 font-semibold transition-colors flex-shrink-0 text-lg"
                    type="button"
                  >
                    😊
                  </button>
                  
                  {/* Emoji Picker Panel */}
                  {showEmojiPicker && (
                    <div className="absolute bottom-14 left-0 bg-[#1a1a2e] border border-purple-400/30 rounded-lg p-3 shadow-2xl z-50 grid grid-cols-8 gap-2 w-80">
                      {['😊', '😂', '❤️', '👍', '🎉', '🔥', '✨', '💯', '🤔', '😎', '🙏', '👏', '💪', '🎯', '📚', '✅', '❌', '⚡', '🌟', '💡', '🎨', '🎵', '🎮', '☕', '🍕', '🚀', '💻', '📱', '⏰', '📝', '✏️', '📖'].map(emoji => (
                        <button
                          key={emoji}
                          type="button"
                          onClick={() => {
                            setChatDraft(chatDraft + emoji);
                            setShowEmojiPicker(false);
                          }}
                          className="text-2xl hover:bg-purple-500/20 rounded p-1 transition-colors"
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  )}
                  
                  <textarea
                    value={chatDraft}
                    onChange={(e) => setChatDraft(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSendChat();
                      }
                    }}
                    placeholder="Type a message... (Shift+Enter for new line)"
                    className="flex-1 rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-emerald-400 resize-none max-h-32 min-h-[40px]"
                    rows={1}
                    style={{ overflowY: 'auto' }}
                  />
                  <button
                    onClick={handleSendChat}
                    className="px-4 py-2 rounded-lg bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30 font-semibold text-sm self-end"
                    type="button"
                  >
                    Send
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Agenda */}
          <div className="rounded-2xl border border-white/10 bg-black/30 p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase tracking-wider text-emerald-200 font-semibold">Agenda</p>
              <button
                onClick={() => setShowAgendaEditor(!showAgendaEditor)}
                className="text-xs text-emerald-200 hover:text-emerald-100"
              >
                {showAgendaEditor ? "Done" : "Edit"}
              </button>
            </div>

            {showAgendaEditor ? (
              <div className="space-y-2">
                {editableAgenda.map((item, idx) => (
                  <div key={idx} className="flex gap-2">
                    <input
                      type="text"
                      value={item}
                      onChange={(e) => {
                        const newAgenda = [...editableAgenda];
                        newAgenda[idx] = e.target.value;
                        setEditableAgenda(newAgenda);
                      }}
                      className="flex-1 rounded-lg border border-white/10 bg-black/40 px-2 py-1 text-xs text-slate-200 focus:outline-none focus:border-emerald-400"
                    />
                    <button
                      onClick={() => setEditableAgenda(editableAgenda.filter((_, i) => i !== idx))}
                      className="text-red-400 hover:text-red-300 text-xs"
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => setEditableAgenda([...editableAgenda, "New item"])}
                  className="text-xs text-emerald-300 hover:text-emerald-200"
                >
                  + Add Item
                </button>
              </div>
            ) : (
              <ul className="space-y-2 text-sm text-slate-200">
                {editableAgenda.length === 0 ? (
                  <li className="text-slate-400 italic text-xs">No agenda items yet</li>
                ) : (
                  editableAgenda.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="mt-1 h-2 w-2 rounded-full bg-emerald-300 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))
                )}
              </ul>
            )}
          </div>

          {/* Snack Order Tracker (replaces Session Notes) */}
          <SnackOrderTracker
            orders={activeOrders}
            onMarkArrived={handleOrderArrived}
            onMarkDelayed={handleOrderDelayed}
            onCheckStatus={handleCheckOrderStatus}
            onOpenSnackPanel={() => setShowSnackPanel(true)}
          />
        </div>
      </div>

      {/* Order ETA Modal */}
      {pendingOrder && (
        <OrderETAModal
          isOpen={showETAModal}
          restaurantName={pendingOrder.restaurant}
          itemName={pendingOrder.item}
          onConfirm={handleETAConfirm}
          onCancel={() => {
            setShowETAModal(false);
            setPendingOrder(null);
          }}
        />
      )}

      {/* Snack Panel */}
      {showSnackPanel && (
        <div className="fixed inset-0 z-[10000] bg-black/80 backdrop-blur-sm flex items-center justify-center p-8">
          <div className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-[#1a1a2e] to-[#16213e] rounded-2xl p-6">
            <button
              onClick={() => setShowSnackPanel(false)}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 text-white hover:bg-white/20 flex items-center justify-center"
            >
              <X className="w-4 h-4" />
            </button>
            <LateNightSnackPanel
              spots={snackSpots}
              loading={snackLoading}
              onCreate={onCreateSnack ? async (spot) => { await Promise.resolve(onCreateSnack(spot as any)); } : async () => {}}
              onUpdate={onUpdateSnack ? async (id, updates) => { await Promise.resolve(onUpdateSnack(String(id), updates)); } : async () => {}}
              onDelete={onDeleteSnack ? async (id) => { await Promise.resolve(onDeleteSnack(String(id))); } : async () => {}}
              onMarkOrdered={onMarkSnackOrdered ? async (id) => { await Promise.resolve(onMarkSnackOrdered(String(id))); } : async () => {}}
              onOrderClick={handleSnackOrdered}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentVirtualRoom;
